import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import { fr } from "date-fns/locale";
import { ChevronProps, DayPicker, DayPickerProps } from "react-day-picker";

import { addYears } from "date-fns";
import { cn } from "../../../utils";
import { AcExpoDayPickerMode, CalendarCaption } from "./";
import { buttonVariants } from "./../Button";

export type CalendarDayPickerProps = DayPickerProps & {
  customMode?: AcExpoDayPickerMode;
};

export const CalendarDayPicker = ({
  className,
  classNames,
  showOutsideDays = true,
  endMonth,
  customMode,
  ...props
}: CalendarDayPickerProps) => {
  const isRangeLike = props.mode === "range" || customMode === "multiple-range";

  return (
    <DayPicker
      locale={fr}
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      endMonth={endMonth ?? addYears(new Date(), 20)}
      classNames={{
        months:
          "relative flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4 first-of-type:!ml-0",
        month_caption: "flex h-7 items-center justify-center px-9",
        dropdowns: "flex items-center gap-1 text-sm font-medium",
        nav: "pointer-events-none absolute inset-x-0 top-0 flex w-full items-center justify-between",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "pointer-events-auto h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "pointer-events-auto h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        caption_label: "hidden",
        month_grid: "w-full border-collapse space-y-1",
        weekdays: "flex",
        weekday:
          "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
        week: "flex w-full mt-2",
        day: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md",
          isRangeLike
            ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
            : "[&:has([aria-selected])]:rounded-md"
        ),
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "h-8 w-8 p-0 font-normal aria-selected:opacity-100"
        ),
        range_start: "day-range-start",
        range_end: "day-range-end",
        selected:
          "bg-gray-800 text-white hover:bg-gray-800 hover:text-white focus:bg-gray-800 focus:text-white",
        today: "bg-accent text-accent-foreground",
        outside:
          "day-outside text-muted-foreground opacity-50  aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        disabled: "text-muted-foreground opacity-50",
        range_middle:
          "aria-selected:bg-[#E0F2FE] aria-selected:text-accent-foreground",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        MonthCaption: CalendarCaption,
        Chevron: ({ ...props }: ChevronProps) => {
          if (props.orientation === "left") {
            return <ChevronLeftIcon className="size-4" />;
          } else {
            return <ChevronRightIcon className="size-4" />;
          }
        },
      }}
      captionLayout="dropdown"
      {...props}
    />
  );
};
